/* ORAGULI — theme interactions
   Vanilla JS, no dependencies. Safe on Theme Editor section reloads.
   All motion is gated on [data-motion="on"] AND prefers-reduced-motion. */
(function () {
  'use strict';

  var reduceMQ = window.matchMedia('(prefers-reduced-motion: reduce)');
  function motionOn() {
    return document.documentElement.getAttribute('data-motion') === 'on' && !reduceMQ.matches;
  }

  /* ---------- Scroll reveals ----------
     Robust by design: in-view elements reveal immediately, an
     IntersectionObserver handles the rest efficiently, and a passive
     scroll fallback guarantees reveal even if the observer never fires. */
  var io = null;
  var revealFallbackBound = false;
  function inViewport(el) {
    var r = el.getBoundingClientRect();
    var h = window.innerHeight || document.documentElement.clientHeight;
    return r.top < h * 0.92 && r.bottom > 0;
  }
  function revealVisible() {
    var pending = document.querySelectorAll('.reveal:not(.is-in), .reveal-line:not(.is-in)');
    Array.prototype.forEach.call(pending, function (el) {
      if (inViewport(el)) el.classList.add('is-in');
    });
  }
  function initReveals(root) {
    root = root || document;
    var els = root.querySelectorAll('.reveal, .reveal-line');
    if (!motionOn()) {
      Array.prototype.forEach.call(els, function (el) { el.classList.add('is-in'); });
      return;
    }
    // Reveal whatever is already on screen right now.
    Array.prototype.forEach.call(els, function (el) {
      if (!el.classList.contains('is-in') && inViewport(el)) el.classList.add('is-in');
    });
    // Observe the rest (efficient path).
    if ('IntersectionObserver' in window) {
      if (!io) {
        io = new IntersectionObserver(function (entries) {
          entries.forEach(function (e) {
            if (e.isIntersecting) {
              e.target.classList.add('is-in');
              io.unobserve(e.target);
            }
          });
        }, { rootMargin: '0px 0px -10% 0px', threshold: 0.08 });
      }
      Array.prototype.forEach.call(els, function (el) {
        if (!el.classList.contains('is-in')) io.observe(el);
      });
    }
    // Scroll fallback — fires reliably in every browser.
    if (!revealFallbackBound) {
      revealFallbackBound = true;
      var ticking = false;
      window.addEventListener('scroll', function () {
        if (!ticking) { requestAnimationFrame(function () { ticking = false; revealVisible(); }); ticking = true; }
      }, { passive: true });
    }
  }

  /* ---------- Header behaviour ----------
     Re-acquires the header element on every init so Theme Editor section
     reloads rebind to the fresh node (no permanent one-time flag). Global
     listeners attach once and always operate on the current header. */
  var headerEl = null;
  var headerLast = 0;
  var headerTicking = false;
  var headerGlobalsBound = false;
  function headerUpdate() {
    headerTicking = false;
    if (!headerEl) return;
    var y = window.pageYOffset;
    headerEl.setAttribute('data-scrolled', y > 8 ? 'true' : 'false');
    if (y > headerLast && y > 220) headerEl.setAttribute('data-hidden', 'true');
    else headerEl.setAttribute('data-hidden', 'false');
    headerLast = y;
  }
  function initHeader() {
    headerEl = document.querySelector('[data-header]');
    if (!headerEl) return;
    headerLast = window.pageYOffset;
    if (!headerGlobalsBound) {
      headerGlobalsBound = true;
      window.addEventListener('scroll', function () {
        if (!headerTicking) { requestAnimationFrame(headerUpdate); headerTicking = true; }
      }, { passive: true });
      // Keyboard focus must never land inside an off-screen (auto-hidden)
      // header: reveal it the moment any header control receives focus.
      document.addEventListener('focusin', function (e) {
        if (headerEl && headerEl.contains(e.target)) headerEl.setAttribute('data-hidden', 'false');
      });
    }
    headerUpdate();
  }

  /* ---------- Mobile drawer ---------- */
  function initDrawer() {
    var drawer = document.querySelector('[data-drawer]');
    if (!drawer || drawer.__oInit) return;
    drawer.__oInit = true;

    var openers = document.querySelectorAll('[data-drawer-open]');
    var closers = drawer.querySelectorAll('[data-drawer-close]');
    var overlay = drawer.querySelector('[data-drawer-overlay]');
    var panel = drawer.querySelector('.drawer__panel');
    var lastFocus = null;

    function focusables() {
      return panel.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])');
    }
    function open(trigger) {
      lastFocus = trigger || document.activeElement;
      drawer.setAttribute('data-open', 'true');
      document.body.style.overflow = 'hidden';
      Array.prototype.forEach.call(openers, function (o) { o.setAttribute('aria-expanded', 'true'); });
      var f = focusables();
      if (f.length) f[0].focus();
      document.addEventListener('keydown', onKey);
    }
    function close() {
      drawer.setAttribute('data-open', 'false');
      document.body.style.overflow = '';
      Array.prototype.forEach.call(openers, function (o) { o.setAttribute('aria-expanded', 'false'); });
      document.removeEventListener('keydown', onKey);
      if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }
    function onKey(e) {
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      var f = focusables();
      if (!f.length) return;
      var first = f[0], lastEl = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); lastEl.focus(); }
      else if (!e.shiftKey && document.activeElement === lastEl) { e.preventDefault(); first.focus(); }
    }

    Array.prototype.forEach.call(openers, function (o) {
      o.addEventListener('click', function () { open(o); });
    });
    Array.prototype.forEach.call(closers, function (c) { c.addEventListener('click', close); });
    if (overlay) overlay.addEventListener('click', close);
  }

  /* ---------- Hero / decorative parallax ---------- */
  var parallaxEls = [];
  var parallaxBound = false;
  var pTicking = false;
  function collectParallax() {
    parallaxEls = Array.prototype.slice.call(document.querySelectorAll('[data-parallax]'));
  }
  function parallaxUpdate() {
    pTicking = false;
    if (!motionOn()) {
      parallaxEls.forEach(function (el) { el.style.transform = ''; });
      return;
    }
    var vh = window.innerHeight;
    parallaxEls.forEach(function (el) {
      var speed = parseFloat(el.getAttribute('data-parallax')) || 0.08;
      var rect = el.getBoundingClientRect();
      if (rect.bottom < -240 || rect.top > vh + 240) return;
      var center = rect.top + rect.height / 2;
      var offset = (center - vh / 2) * speed * -1;
      el.style.transform = 'translate3d(0,' + offset.toFixed(1) + 'px,0)';
    });
  }
  function onScrollParallax() {
    if (!pTicking) { requestAnimationFrame(parallaxUpdate); pTicking = true; }
  }
  function initParallax() {
    collectParallax();
    if (!parallaxBound) {
      parallaxBound = true;
      window.addEventListener('scroll', onScrollParallax, { passive: true });
      window.addEventListener('resize', onScrollParallax, { passive: true });
    }
    parallaxUpdate();
  }

  /* ---------- Product media gallery ---------- */
  function initProductGallery(root) {
    var galleries = (root || document).querySelectorAll('[data-product-gallery]');
    Array.prototype.forEach.call(galleries, function (gallery) {
      if (gallery.__oInit) return;
      gallery.__oInit = true;
      var thumbs = gallery.querySelectorAll('[data-thumb]');
      var stage = gallery.querySelector('.pdp__stage');
      if (!stage) return;
      function show(id) {
        id = String(id);
        Array.prototype.forEach.call(stage.querySelectorAll('[data-media-id]'), function (m) {
          var match = m.getAttribute('data-media-id') === id;
          m.hidden = !match;
          m.classList.toggle('is-active', match);
          if (!match) { var v = m.querySelector('video'); if (v && v.pause) { try { v.pause(); } catch (e) {} } }
        });
        Array.prototype.forEach.call(thumbs, function (t) {
          var on = t.getAttribute('data-thumb') === id;
          t.classList.toggle('is-active', on);
          if (on) t.setAttribute('aria-current', 'true'); else t.removeAttribute('aria-current');
        });
      }
      Array.prototype.forEach.call(thumbs, function (t) {
        t.addEventListener('click', function () { show(t.getAttribute('data-thumb')); });
      });
      gallery.__showMedia = show;
    });
  }

  /* ---------- Product form: variant-aware price & availability ---------- */
  function initProductForm(root) {
    var sections = (root || document).querySelectorAll('[data-product-section]');
    Array.prototype.forEach.call(sections, function (sec) {
      if (sec.__oProduct) return;
      sec.__oProduct = true;
      var dataEl = sec.querySelector('[data-variant-json]');
      if (!dataEl) return;
      var variants;
      try { variants = JSON.parse(dataEl.textContent); } catch (e) { return; }
      if (!variants || !variants.length) return;

      var selectors = sec.querySelectorAll('[data-option-selector]');
      var idInput = sec.querySelector('[data-variant-id]');
      var priceEl = sec.querySelector('[data-price]');
      var availEl = sec.querySelector('[data-availability]');
      var addBtn = sec.querySelector('[data-add-button]');
      var addLabel = sec.querySelector('[data-add-label]');
      var gallery = sec.querySelector('[data-product-gallery]');
      var S = {
        add: sec.getAttribute('data-str-add') || 'Add to cart',
        soldout: sec.getAttribute('data-str-soldout') || 'Sold out',
        unavailable: sec.getAttribute('data-str-unavailable') || 'Unavailable',
        instock: sec.getAttribute('data-str-instock') || 'In stock'
      };

      function current() {
        if (!selectors.length) return variants[0];
        var chosen = [];
        Array.prototype.forEach.call(selectors, function (s) {
          chosen[parseInt(s.getAttribute('data-option-index'), 10)] = s.value;
        });
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i], ok = true;
          for (var j = 0; j < chosen.length; j++) { if (v.options[j] !== chosen[j]) { ok = false; break; } }
          if (ok) return v;
        }
        return null;
      }
      function update() {
        var v = current();
        if (!v) {
          if (addBtn) addBtn.disabled = true;
          if (addLabel) addLabel.textContent = S.unavailable;
          return;
        }
        if (idInput) idInput.value = v.id;
        if (priceEl) priceEl.innerHTML = v.compare_at ? '<del>' + v.compare_at + '</del> <ins>' + v.price + '</ins>' : v.price;
        if (availEl) availEl.innerHTML = '<span class="pdp__stock pdp__stock--' + (v.available ? 'in' : 'out') + '">' + (v.available ? S.instock : S.soldout) + '</span>';
        if (addBtn) addBtn.disabled = !v.available;
        if (addLabel) addLabel.textContent = v.available ? S.add : S.soldout;
        if (v.media_id && gallery && gallery.__showMedia) gallery.__showMedia(v.media_id);
      }
      Array.prototype.forEach.call(selectors, function (s) { s.addEventListener('change', update); });
    });
  }

  /* ---------- Boot ---------- */
  function initAll(root) {
    initReveals(root);
    initHeader();
    initDrawer();
    initParallax();
    initProductGallery(root);
    initProductForm(root);
  }

  if (document.readyState !== 'loading') initAll();
  else document.addEventListener('DOMContentLoaded', function () { initAll(); });

  // Safety sweeps: guarantee reveals fire after full load and when a tab
  // that loaded in the background (rAF/observers paused) becomes visible.
  window.addEventListener('load', function () { revealVisible(); });
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState === 'visible') { initReveals(); parallaxUpdate(); }
  });

  /* Theme Editor lifecycle */
  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
  document.addEventListener('shopify:section:reorder', function () { initReveals(); collectParallax(); });

  if (reduceMQ.addEventListener) {
    reduceMQ.addEventListener('change', function () { initReveals(); parallaxUpdate(); });
  }
})();
